import { toast } from "react-toastify";
import { APP_CONFIG } from "../../../constants/constants";
import i18n from "../../../services/i18n";
import { ColorRGB } from "../../../types/common";
import { TGeozoneMapDataPolygon, TLatLng } from "../../../types/map";
import { basicDebounce } from "../../../utils/common";
import { getCircleLocs, renderPolygon } from "../../../utils/map";
import { mapInfoBoxContent } from "../../map-fleet/common";
import { TAssetLocationDataPoint } from "../../map-fleet/type";
import { TMapOperationsProps, TMapUpdatesHandler } from "./type";

const dragLabelCenter = {
  // subTitle: 'to reposition',
  // title: 'Drag',
};
const dragLabelPoints = {
  // subTitle: 'to reshape',
  // title: 'Drag',
};

export const mapOperations = ({
  drawShapeOnly = false,
  ...props
}: TMapOperationsProps) => {
  const google = (window as any).google;
  (window as any).map = props.mapRef.current;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mPushpins: {
      pCentre: null,
      pPoints: [],
    },
    mPolygon: null,
    mInfobox: null,
    circleRadius:
      (props.mapData as TGeozoneMapDataPolygon).locs ??
      APP_CONFIG.MAPS.DEFAULTS.RADIUS,
  };

  const renderMapObjects = async () => {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const color = (props.mapData.color ??
      APP_CONFIG.MAPS.DEFAULTS.ZONE_COLOR) as ColorRGB;
    let refCenter: google.maps.LatLng = new google.maps.LatLng(
      (props.mapData as TGeozoneMapDataPolygon).centerPosition.latitude,
      (props.mapData as TGeozoneMapDataPolygon).centerPosition.longitude
    );
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log("Dropping center at", refCenter.lat(), refCenter.lng());
    if (!drawShapeOnly)
      props.mapRef.current.map.setOptions({ center: refCenter }); // set the view to the center

    // TODO: uncertain if polygon can have a centre pushpin
    // create center pushpin
    if (!drawShapeOnly) {
      props.mapRef.current.objects.mPushpins.pCentre =
        new AdvancedMarkerElement({
          position: refCenter,
          map: props.mapData.editable ? props.mapRef.current.map : null,
          gmpDraggable: true, // props.mapData.editable,
          ...(props.mapData.editable ? dragLabelCenter : {}),
        });
      // set to null as center marker is not required for polygon
      props.mapRef.current.objects.mPushpins.pCentre.setMap(null);
    }

    // ------------------------------------------------------------------------------------------------------------
    // TODO: when editing a polygon, we identified that extra pushpins is added to the map - this needs to be fixed
    // ------------------------------------------------------------------------------------------------------------

    // let initialPoints =
    // initialPoints.pop(); // remove the last point to avoid duplication
    // load from saved polygon points or get the default polygon points
    const polygonPoints =
      !!(props.mapData as TGeozoneMapDataPolygon).locs &&
      (props.mapData as TGeozoneMapDataPolygon).locs.length > 2
        ? (props.mapData as TGeozoneMapDataPolygon).locs.map(
            (point: TLatLng) =>
              new google.maps.LatLng(point.latitude, point.longitude)
          )
        : getCircleLocs(
            refCenter,
            props.mapRef.current.objects.circleRadius,
            APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
            true
          );
    if (APP_CONFIG.DEBUG.GEOZONES) console.log("polygonPoints", polygonPoints);

    // render the polygon
    const polygonLayer = renderPolygon(
      props.mapRef,
      refCenter,
      polygonPoints,
      color,
      drawShapeOnly
    );
    // add an infobox to the center of the shape, that shows on hover
    if (drawShapeOnly && !!props.geozoneId) {
      const infobox = new google.maps.InfoWindow({
        content: mapInfoBoxContent(
          { deviceid: props.geozoneId } as TAssetLocationDataPoint,
          true
        ),
        position: refCenter, // used to identify position of the infobox
        headerDisabled: true,
        zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
      });
      const mouseoverHandler = (e: any) => {
        const currentZoom = props.mapRef.current.map.getZoom();
        infobox.setOptions({
          disableAutoPan: currentZoom >= APP_CONFIG.MAPS.ZOOM_MAX - 2,
        });
        infobox.setPosition(
          props.mapRef.current.map.getBounds().contains(refCenter) &&
            currentZoom < APP_CONFIG.MAPS.ZOOM_MAX - 2
            ? refCenter // center of the geozone bounds
            : true
              ? new google.maps.LatLng(e.latLng.lat(), e.latLng.lng()) // position of the cursor
              : props.mapRef.current.map.getCenter() // map viewport center
        );
        infobox.open({
          // anchor: circleLayer,
          map: props.mapRef.current.map,
        });
        // Close info box after 4 sec to let user interact with other markers
        setTimeout(() => {
          infobox.close();
        }, 4000);
      };
      const debouncedMouseoverHandler = basicDebounce(
        mouseoverHandler,
        1000 * 1
      );
      if (!!polygonLayer) {
        google.maps.event.addListener(
          polygonLayer,
          "mouseover",
          debouncedMouseoverHandler
        );
        google.maps.event.addListener(polygonLayer, "mouseout", (e: any) => {
          infobox.close();
        });
      }
    }

    if (drawShapeOnly) return;

    // polygonPoints.pop(); // causing issues with the polygon
    // use the polygon points to create pushpins
    polygonPoints?.forEach((polygonPoint: any, polygonPointIndex: number) => {
      // const newPushpin = new Microsoft.Maps.Pushpin(polygonPoint, {
      //   anchor: new Microsoft.Maps.Point(16, 32),
      //   icon: svgIcons.MapMarkerRed,
      //   visible: props.mapData.editable,
      //   draggable: props.mapData.editable,
      //   ...(props.mapData.editable ? dragLabelPoints : {}),
      // });
      const newPushpin = new AdvancedMarkerElement({
        gmpDraggable: true, // props.mapData.editable,
        map: props.mapData.editable ? props.mapRef.current.map : null,
        position: polygonPoint,
        ...(props.mapData.editable ? dragLabelPoints : {}),
      });
      // props.mapRef.current.map.entities.push(newPushpin);

      // add event listener for dragstart event on the edge pushpin
      let originalLocation: any = null;
      // Microsoft.Maps.Events.addHandler(newPushpin, "dragstart", (args: any) => {
      //   originalLocation = newPushpin.getLocation();
      // });
      google.maps.event.addListener(newPushpin, "drag", (args: any) => {
        // originalLocation = newPushpin.getPosition();
        if (originalLocation === null) originalLocation = newPushpin.position;
      });

      // add event listener for dragend event on the edge pushpin
      google.maps.event.addListener(newPushpin, "dragend", (args: any) => {
        console.log("dragend index", polygonPointIndex);
        try {
          // store new location in pPoints
          props.mapRef.current.objects.mPushpins.pPoints[
            polygonPointIndex
          ].position = args.latLng;
          // redraw the polygon
          const polygonPointLocations =
            props.mapRef.current.objects.mPushpins.pPoints.map(
              (point: any) => point.position
            );
          console.log("polygonPointLocations", polygonPointLocations);
          // renderPolygon(props.mapRef, refCenter, polygonPointLocations, color);
          polygonLayer.setPath(polygonPointLocations);
          // update the map data
          console.log("[setMapData] via mapOperations (radius dragend)");
          props.setMapData({
            ...props.mapData,
            centerPosition: {
              latitude: refCenter.lat(),
              longitude: refCenter.lng(),
            },
            locs: polygonPointLocations.map(
              (point: any) =>
                ({
                  latitude: point.lat(),
                  longitude: point.lng(),
                }) as TLatLng
            ),
          });
        } catch (error) {
          console.error(error);
          toast.error(
            i18n.t("admins.geozones.detailsPage.map.polygonError.dragend")
          );
          // Reset the pushpin to its original location
          // newPushpin.setLocation(originalLocation);
          newPushpin.position = originalLocation;
          const polygonPointLocations =
            props.mapRef.current.objects.mPushpins.pPoints.map(
              (point: any) => point.position
            );
          polygonLayer.setPath(polygonPointLocations);
        }
      });

      props.mapRef.current.objects.mPushpins.pPoints.push({
        position: polygonPoint,
        marker: newPushpin,
      });
    });
  };

  // load map modules before rendering the map objects
  renderMapObjects();
};

export const mapUpdatesHandler: TMapUpdatesHandler = (props, action, value) => {
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log("[mapUpdatesHandler]", action, value);

  const Microsoft = (window as any).Microsoft;

  switch (action) {
    case "edit":
      // edge pushpins are draggable
      props.mapRef.current.objects.mPushpins?.pPoints?.forEach(
        (pushpin: { position: google.maps.LatLng; marker: any }) => {
          console.log("PUSHPIN ==");
          console.log(pushpin);

          // following code removes the AdvancedMarkerElement from the map
          // source: https://stackoverflow.com/a/74492809/1268350
          if (value) {
            pushpin.marker.map = props.mapRef.current.map;
          } else {
            pushpin.marker.map = null;
          }
        }
      );
      // show the infobox
      if (false) {
        const refCenter = props.mapRef.current.map?.getCenter();
        props.mapRef.current.objects.mInfobox = new Microsoft.Maps.Infobox(
          refCenter,
          {
            title: i18n.t(
              "admins.geozones.detailsPage.map.polygonEdit.heading"
            ),
            description: i18n.t(
              "admins.geozones.detailsPage.map.polygonEdit.sub_heading"
            ),
            showPointer: false,
            // showCloseButton: false,
            visible: false,
            actions: [
              {
                label: i18n.t(
                  "admins.geozones.detailsPage.map.polygonEdit.ok_button"
                ),
                eventHandler: () => {
                  // close info box
                  props.mapRef.current.objects.mInfobox.setOptions({
                    visible: false,
                  });
                },
              },
            ],
          }
        );
        props.mapRef.current.objects.mInfobox.setMap(props.mapRef.current.map);
        props.mapRef.current.objects.mInfobox.setOptions({ visible: true });
        setTimeout(() => {
          const infoboxElement = (window as any).document.querySelector(
            ".MicrosoftMap .Infobox"
          )?.parentNode;
          if (infoboxElement) {
            (infoboxElement as any).style.transition = "all 0.5s ease 0s";
            (infoboxElement as any).style.left = "20px";
            (infoboxElement as any).style.top = "20px";
          }
        }, 100);
      }
      break;
    default:
      break;
  }
};
